/* eslint-disable react/no-unknown-property */
import FA from 'admin/components/atoms/FontAwesome';
import { downloadCsvFile, toQueryParams } from 'admin/utils';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { QueryParamConfig, StringParam, useQueryParams, withDefault } from 'use-query-params';
import {
  getC2CTransactions,
  getC2CTransactionsCSV,
} from '~/javascripts/admin/actions/transactions';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { Button } from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import FilterForm from './FilterForm';
import ListTable from './ListTable';

type C2CTransfers = Awaited<ReturnType<typeof getC2CTransactions>>;

const FormWrapper = styled.div``;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 20px 0 10px 0;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '取引管理', href: '', isCurrentPage: true },
  { value: 'マネー譲渡履歴', href: '', isCurrentPage: true },
];

const MomentParam: QueryParamConfig<Moment | null> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

const queryParamConfigs = {
  sender_user_id: withDefault(StringParam, ''),
  receiver_user_id: withDefault(StringParam, ''),
  from: withDefault(MomentParam, null),
  to: withDefault(MomentParam, null),
};

const TransactionsC2C = () => {
  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const privateMoneyId = userPrivateMoney.id;

  const [c2cTransfers, setC2CTransfers] = useState<C2CTransfers>();

  const allowedPrivateMoneys = user.isServiceUser() ? [] : user.issuedMoneys();
  const isSelectedMoneyAllowed =
    user.isServiceUser() || allowedPrivateMoneys.some((m) => m.id === userPrivateMoney.id);

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const fetchC2CTransfers = useCallback(
    (queryLink = '') => {
      if (isSelectedMoneyAllowed && privateMoneyId) {
        const queryString =
          queryLink ||
          toQueryParams(
            {
              private_money_id: privateMoneyId,
              ...query,
            },
            true
          );

        getC2CTransactions(queryString).then((res) => setC2CTransfers(res));
      }
    },
    [query, isSelectedMoneyAllowed, privateMoneyId]
  );

  const fetchC2CTransfersCSV = () => {
    if (!privateMoneyId) return;
    getC2CTransactionsCSV(
      toQueryParams(
        {
          private_money_id: privateMoneyId,
          perPage: 10000,
          maxPage: 30,
          ...query,
        },
        true
      )
    ).then((ret) => {
      downloadCsvFile(ret.url);
    });
  };

  const onChangePage = (paginatedQuery: string | null) =>
    fetchC2CTransfers(paginatedQuery ?? undefined);

  useEffect(() => {
    fetchC2CTransfers();
  }, [query, isSelectedMoneyAllowed, fetchC2CTransfers]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      {!isSelectedMoneyAllowed && (
        <>
          <SelectedPrivateMoneyDisplay
            highlightHeader
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <UserPrivateMoneyNotAllowed />
        </>
      )}
      {isSelectedMoneyAllowed && (
        <>
          <SelectedPrivateMoneyDisplay />
          <FilterForm query={query} setQuery={(q) => setQuery({ ...q })} />
          <HeadingWrapper>
            <Headings.h5>検索結果</Headings.h5>
            <Button onClick={fetchC2CTransfersCSV} variant="muted-outline">
              <FA icon="download" /> CSV
            </Button>
          </HeadingWrapper>
          <ListTable c2cTransfers={c2cTransfers} onChangePage={onChangePage} />
        </>
      )}
    </FormWrapper>
  );
};

export default TransactionsC2C;
